import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, RotateCcw, Smartphone, Compass } from "lucide-react";

/**
 * BirthChart3D — an interactive 3D wheel that shows the 12 astrological
 * houses (with the user's Sun / Moon / Rising sign highlighted). Built with
 * pure CSS 3D transforms — no Three.js dependency — so it's a lightweight
 * upgrade for AstrologyRoom / Kundli. Users can drag to rotate.
 *
 * Props:
 *   rashi   — Moon sign name (Aries, Taurus, ...)   e.g. "Karka"
 *   lagna   — rising sign name                        e.g. "Simha"
 *   sun     — optional Sun sign
 *   nakshatra — text badge shown in the center
 */
const SIGNS = [
  { key: "aries",       glyph: "♈", name: "Mesha (Aries)",       color: "from-red-500 to-rose-700" },
  { key: "taurus",      glyph: "♉", name: "Vrishabha (Taurus)",  color: "from-emerald-500 to-green-700" },
  { key: "gemini",      glyph: "♊", name: "Mithuna (Gemini)",    color: "from-yellow-400 to-amber-600" },
  { key: "cancer",      glyph: "♋", name: "Karka (Cancer)",      color: "from-sky-400 to-blue-700" },
  { key: "leo",         glyph: "♌", name: "Simha (Leo)",         color: "from-orange-500 to-red-600" },
  { key: "virgo",       glyph: "♍", name: "Kanya (Virgo)",       color: "from-lime-500 to-emerald-700" },
  { key: "libra",       glyph: "♎", name: "Tula (Libra)",        color: "from-fuchsia-500 to-pink-700" },
  { key: "scorpio",     glyph: "♏", name: "Vrischika (Scorpio)", color: "from-rose-700 to-red-900" },
  { key: "sagittarius", glyph: "♐", name: "Dhanu (Sagittarius)", color: "from-purple-500 to-indigo-700" },
  { key: "capricorn",   glyph: "♑", name: "Makara (Capricorn)",  color: "from-stone-500 to-slate-800" },
  { key: "aquarius",    glyph: "♒", name: "Kumbha (Aquarius)",   color: "from-cyan-400 to-teal-700" },
  { key: "pisces",      glyph: "♓", name: "Meena (Pisces)",      color: "from-indigo-400 to-blue-700" },
];

const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
const findSign = (label) => {
  const n = normalize(label);
  return SIGNS.find(s =>
    n.includes(s.key) || n.includes(s.name.toLowerCase().split(" ")[0].replace(/[^a-z]/g, ""))
  ) || null;
};

export function BirthChart3D({ rashi, lagna, sun, nakshatra, className = "" }) {
  const [tilt, setTilt] = useState({ x: -18, y: 12 });
  const [autoSpin, setAutoSpin] = useState(true);
  const [arMode, setArMode] = useState(false);
  const [arError, setArError] = useState(null);
  const dragRef = useRef(null);
  const dragging = useRef(false);

  // Auto-rotate the wheel so it always feels alive; user drag pauses spin.
  useEffect(() => {
    if (!autoSpin || arMode) return;
    const id = setInterval(() => setTilt(t => ({ ...t, y: (t.y + 0.4) % 360 })), 40);
    return () => clearInterval(id);
  }, [autoSpin, arMode]);

  // AR Sky Mode — map real-time device orientation to chart rotation.
  // On iOS ≥13 we must request permission via a user gesture.
  useEffect(() => {
    if (!arMode) return;
    let disposed = false;
    const onOrient = (e) => {
      if (disposed) return;
      // alpha (0-360): compass heading around Z axis
      // beta  (-180-180): tilt front/back around X
      // gamma (-90-90): tilt left/right around Y
      const alpha = e.alpha ?? 0;
      const beta  = e.beta  ?? 0;
      const gamma = e.gamma ?? 0;
      // Map alpha to Y rotation (device turning → wheel spins),
      // beta to X rotation (tilting phone forward reveals top of chart).
      setTilt({
        x: Math.max(-80, Math.min(30, beta - 90)),
        y: alpha,
      });
    };
    const attach = () => window.addEventListener("deviceorientation", onOrient, true);
    const dor = typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null;
    if (dor && typeof dor.requestPermission === "function") {
      dor.requestPermission()
        .then((state) => {
          if (state === "granted") { attach(); setArError(null); }
          else { setArError("Motion permission denied"); setArMode(false); }
        })
        .catch(() => { setArError("Motion sensors unavailable"); setArMode(false); });
    } else {
      // Non-iOS or non-secure context — try anyway
      attach();
    }
    return () => { disposed = true; window.removeEventListener("deviceorientation", onOrient, true); };
  }, [arMode]);

  const moonSign = useMemo(() => findSign(rashi), [rashi]);
  const risingSign = useMemo(() => findSign(lagna), [lagna]);
  const sunSign = useMemo(() => findSign(sun), [sun]);

  const isHighlighted = (s) =>
    (moonSign && s.key === moonSign.key) ||
    (risingSign && s.key === risingSign.key) ||
    (sunSign && s.key === sunSign.key);

  const highlightLabel = (s) => {
    const tags = [];
    if (moonSign && s.key === moonSign.key)   tags.push("Moon");
    if (risingSign && s.key === risingSign.key) tags.push("Rising");
    if (sunSign && s.key === sunSign.key)     tags.push("Sun");
    return tags.join(" · ");
  };

  const onDown = (e) => {
    dragging.current = { x: e.clientX ?? e.touches?.[0]?.clientX, y: e.clientY ?? e.touches?.[0]?.clientY, tilt };
    setAutoSpin(false);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    const dx = cx - dragging.current.x;
    const dy = cy - dragging.current.y;
    setTilt({ x: dragging.current.tilt.x - dy * 0.4, y: dragging.current.tilt.y + dx * 0.6 });
  };
  const onUp = () => { dragging.current = null; };

  return (
    <div className={`relative select-none ${className}`} data-testid="birthchart-3d">
      <style>{`
        @keyframes bc3d-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(251,191,36,.45); } 50% { box-shadow: 0 0 0 12px rgba(251,191,36,0); } }
        .bc3d-face {
          position: absolute; left: 50%; top: 50%;
          width: 84px; height: 108px; margin-left: -42px; margin-top: -54px;
          border-radius: 12px;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          font-weight: 900; text-align: center;
          color: white;
          border: 1px solid rgba(255,255,255,.15);
          backdrop-filter: blur(4px);
        }
      `}</style>

      {/* stage */}
      <div
        ref={dragRef}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        className="relative mx-auto cursor-grab active:cursor-grabbing"
        style={{
          width: 320, height: 320,
          perspective: "1000px",
          touchAction: "none",
        }}
        data-testid="birthchart-3d-stage"
      >
        {/* Ecliptic ring — the reference plane */}
        <div className="absolute inset-4 rounded-full border-2 border-amber-400/20"
             style={{ transform: `rotateX(80deg) rotateZ(${-tilt.y}deg)`, transformStyle: "preserve-3d" }}/>

        {/* Wheel */}
        <div
          className="absolute inset-0"
          style={{
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            transformStyle: "preserve-3d",
            transition: dragging.current ? "none" : "transform 60ms linear",
          }}
        >
          {SIGNS.map((s, i) => {
            const angle = (i * 360) / 12;
            const active = isHighlighted(s);
            return (
              <div key={s.key}
                   className={`bc3d-face bg-gradient-to-b ${s.color} ${active ? "ring-2 ring-amber-300" : "opacity-80"}`}
                   style={{
                     transform: `rotateY(${angle}deg) translateZ(140px)`,
                     boxShadow: active ? "0 0 24px rgba(251,191,36,.55)" : "0 6px 16px rgba(0,0,0,.35)",
                     animation: active ? "bc3d-pulse 2s ease-out infinite" : undefined,
                   }}>
                <div className="text-3xl leading-none">{s.glyph}</div>
                <div className="text-[9px] uppercase tracking-widest opacity-90 mt-1">
                  {s.name.split(" ")[0]}
                </div>
                {active && (
                  <div className="text-[8px] mt-1 bg-black/40 rounded-full px-1.5 py-0.5">
                    {highlightLabel(s)}
                  </div>
                )}
              </div>
            );
          })}

          {/* Center orb */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
               style={{ transform: `translate(-50%,-50%) rotateY(${-tilt.y}deg) rotateX(${-tilt.x}deg)` }}>
            <div className="rounded-full bg-gradient-to-br from-amber-300 via-orange-400 to-rose-500 w-24 h-24 grid place-items-center border-4 border-white/30 shadow-2xl">
              <div className="text-center">
                <Sparkles size={14} className="mx-auto text-white"/>
                <div className="text-[9px] uppercase tracking-widest text-white/90 mt-1 font-black">Nakshatra</div>
                <div className="text-[10px] text-white font-black leading-tight px-1">{nakshatra || "—"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Cardinal direction pips (fixed to the outer frame) */}
        {["N", "E", "S", "W"].map((c, i) => (
          <div key={c} className="absolute text-[9px] text-amber-300/60 font-black"
               style={{
                 left: i === 1 ? "auto" : "50%", right: i === 1 ? 0 : "auto",
                 top:  i === 2 ? "auto" : (i === 0 ? 0 : "50%"),
                 bottom: i === 2 ? 0 : "auto",
                 transform: i === 0 || i === 2 ? "translate(-50%,0)" : "translate(0,-50%)",
               }}>{c}</div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-3 gap-2">
        <div className="text-[10px] uppercase font-black text-amber-300 flex items-center gap-1">
          {arMode
            ? <><Compass size={11}/> AR sky · point your phone at the sky</>
            : <><Sparkles size={11}/> Drag to rotate · your Moon, Sun & Rising glow</>}
        </div>
        <div className="flex gap-1">
          <button onClick={() => setArMode(m => !m)}
                  className={`text-[10px] uppercase font-black flex items-center gap-1 rounded-full px-2 py-1
                              ${arMode ? "bg-amber-400 text-stone-900" : "bg-white/10 text-white/80 hover:text-white"}`}
                  data-testid="birthchart-3d-ar">
            <Smartphone size={11}/> {arMode ? "AR ON" : "AR Sky"}
          </button>
          <button onClick={() => { setTilt({ x: -18, y: 12 }); setAutoSpin(true); setArMode(false); }}
                  className="text-[10px] uppercase font-black text-white/70 hover:text-white flex items-center gap-1 rounded-full px-2 py-1 bg-white/5"
                  data-testid="birthchart-3d-reset">
            <RotateCcw size={11}/> Reset
          </button>
        </div>
      </div>
      {arError && (
        <div className="text-[10px] text-rose-400 text-center mt-1" data-testid="birthchart-3d-ar-error">{arError}</div>
      )}
      {arMode && !arError && (
        <div className="text-[10px] text-amber-300/70 text-center mt-1">Best on a mobile device with gyroscope · tilt & rotate your phone toward the sky.</div>
      )}
    </div>
  );
}

export default BirthChart3D;
